import React, { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import ProgressDot from '../../../shared/ProgressDot';
import NextStepButton from '../../../shared/NextStepButton';
import { salesOrderSteps } from './salesOrderSteps';
import './SalesOrderStepper.css';

// The Sales Order header's progress stepper (issue #310): Draft →
// Submitted → Accepted → Pick → Pack → Ship, in place of the status pill. Same "follow
// the bouncing ball" idea as Pick, Pack and Ship's own stepper
// (fulfilmentGuide.js): worked out fresh from the order as it is now, with
// no record of steps taken, so Withdraw Acceptance, Revise or a shipment
// reversal simply show it back at the matching step. A guide only — it
// shows where the order is and explains each step, and never hides,
// disables or blocks an action. The next step itself is the header's Next
// button (SalesOrderNextButton below), beside the Actions menu. Status
// History stays the record of what happened. The steps themselves are
// worked out in salesOrderSteps.js.

// Help shown on hover with a mouse, and on tap on a touch screen (a
// native title tooltip never shows on touch). A mouse click pins it open
// rather than toggling it shut under the pointer. Leaving the step closes
// it after a moment, unless the pointer has moved onto the help itself.
function HelpPopover({ label, className, trigger, children }) {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const pointer = useRef('mouse');
  const closeTimer = useRef(null);
  const cancelClose = () => clearTimeout(closeTimer.current);
  const close = () => {
    cancelClose();
    pinned.current = false;
    setOpen(false);
  };
  const closeSoon = (e) => {
    if (e.pointerType !== 'mouse' || pinned.current) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(() => cancelClose, []);
  return (
    <Popover.Root open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={className}
          onPointerDown={(e) => { pointer.current = e.pointerType; }}
          onPointerEnter={(e) => { if (e.pointerType === 'mouse') { cancelClose(); setOpen(true); } }}
          onPointerLeave={closeSoon}
          onClick={(e) => {
            if (pointer.current === 'mouse') {
              e.preventDefault();
              pinned.current = true;
              setOpen(true);
            }
          }}
        >
          {trigger}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="so-stepper-help"
          side="bottom"
          align="center"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerEnter={cancelClose}
          onPointerLeave={closeSoon}
        >
          {children}
          <Popover.Arrow className="so-stepper-help-arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// What to do next, as text only: the Next button beside the Actions menu
// does it, so the help has one job, explaining.
function NextLine({ guide }) {
  if (!guide.next) return null;
  return (
    <div className="so-stepper-help-next">
      {guide.next}
      {guide.hold && guide.holdReason && <div className="so-stepper-help-reason">Reason: {guide.holdReason}</div>}
    </div>
  );
}

// The header's Next button: the one way to take the step the order is at
// ("Accept Order →", or "Release Hold →" On Hold). `actions` holds the
// page's own handlers by step key ('draft' for Submit, 'accepted', 'pick',
// 'pack', 'ship', 'hold'), confirmations and all; a step the user may not
// take isn't passed in, and then no button shows.
export function SalesOrderNextButton({ order, actions }) {
  const guide = salesOrderSteps(order);
  const action = guide.nextKey && actions?.[guide.nextKey];
  if (!action || !guide.nextLabel) return null;
  return <NextStepButton label={guide.nextLabel} hold={guide.hold} disabled={action.disabled} onClick={action.onSelect} />;
}

function Tags({ guide }) {
  return (
    <>
      {guide.hold && <span className="so-stepper-tag hold" title={guide.holdReason ? `On Hold: ${guide.holdReason}` : 'On Hold'}>Hold</span>}
      {guide.complete && <span className="so-stepper-tag complete">Complete</span>}
    </>
  );
}

function stepClass(guide, i) {
  if (i !== guide.current) return 'so-stepper-step';
  return `so-stepper-step current${guide.hold ? ' hold' : ''}`;
}

function SalesOrderStepper({ order }) {
  const guide = salesOrderSteps(order);
  const currentStep = guide.current === null ? null : guide.steps[guide.current];
  return (
    <div className="so-stepper">
      <div className="so-stepper-full">
        <Tags guide={guide} />
        {guide.steps.map((step, i) => (
          <React.Fragment key={step.key}>
            {i > 0 && <span className="so-stepper-sep" aria-hidden="true">›</span>}
            <HelpPopover
              label={`${step.label}: ${step.progress}`}
              className={stepClass(guide, i)}
              trigger={(
                <>
                  <span className="so-stepper-dot"><ProgressDot state={step.state} /></span>
                  <span className="so-stepper-label">{step.label}</span>
                </>
              )}
            >
              <div className="so-stepper-help-head">{step.label}</div>
              {step.meaning && <div>{step.meaning}</div>}
              <div className="so-stepper-help-progress">{step.progress}</div>
              {i === guide.current && <NextLine guide={guide} />}
            </HelpPopover>
          </React.Fragment>
        ))}
      </div>
      <div className="so-stepper-compact">
        <Tags guide={guide} />
        <HelpPopover
          label={currentStep ? `Step ${guide.current + 1} of ${guide.steps.length}: ${currentStep.label}` : 'Complete'}
          className={guide.current === null ? 'so-stepper-step' : stepClass(guide, guide.current)}
          trigger={(
            <>
              <span className="so-stepper-dots">
                {guide.steps.map((step) => <ProgressDot key={step.key} state={step.state} />)}
              </span>
              {currentStep && (
                <span className="so-stepper-label">Step {guide.current + 1} of {guide.steps.length} · {currentStep.label}</span>
              )}
            </>
          )}
        >
          {guide.steps.map((step, i) => (
            <div key={step.key} className={`so-stepper-help-row${i === guide.current ? ' current' : ''}`}>
              <ProgressDot state={step.state} />
              <span><strong>{step.label}</strong> — {step.progress}</span>
            </div>
          ))}
          {currentStep?.meaning && <div className="so-stepper-help-progress">{currentStep.meaning}</div>}
          <NextLine guide={guide} />
        </HelpPopover>
      </div>
    </div>
  );
}

export default SalesOrderStepper;
